import { useState, useEffect, useCallback } from "react";
import Bowser from "bowser";

const useDeviceAnalytics = () => {
  const [analyticsData, setAnalyticsData] = useState({
    latitude: null,
    longitude: null,
    browserName: null,
    deviceName: null,
    network: null,
    isLoading: true,
    error: null,
  });

  // Exposed as `refetch` so callers can re-run this after the user grants a
  // permission that was denied on the first (mount) pass — e.g. location
  // enabled via the pre-call modal. Without this, a mount-time geolocation
  // failure leaves `error`/lat-lng stale forever and gates "Start Your Journey".
  const fetchAnalytics = useCallback(async () => {
    setAnalyticsData((prev) => ({ ...prev, isLoading: true, error: null }));
    try {
        let browserName = null;
        let deviceName = null;
        let network = null;
        let latitude = null;
        let longitude = null;
        let errorMessages = [];

        try {
          // Get browser and device information
          const userAgent = navigator?.userAgent || "";
          const parsedUserAgent = Bowser.parse(userAgent);
          // Remove engine field from parsed object
          delete parsedUserAgent.engine;

          browserName = parsedUserAgent.browser.name;
          deviceName = parsedUserAgent.os.name;
        } catch (error) {
          errorMessages.push("Failed to fetch browser/device details");
        }

        try {
          // Get network information
          const connection = navigator.connection || navigator.networkInformation || null;
          network = connection?.effectiveType || "unknown";
        } catch (error) {
          errorMessages.push("Failed to fetch network details");
        }

        try {
          // Get location
          const getLocation = () =>
            new Promise((resolve, reject) => {
              if ("geolocation" in navigator) {
                navigator.geolocation.getCurrentPosition(
                  (position) => {
                    const { latitude: lat, longitude: lon } = position.coords;
                    resolve({ latitude: lat, longitude: lon });
                  },
                  (err) => {
                    console.error(`Geolocation error: ${err.message}`);
                    reject(err);
                  },
                  {
                    enableHighAccuracy: true,
                    timeout: 10000,
                    maximumAge: 0,
                  }
                );
              } else {
                errorMessages.push(`Geolocation is not supported`);
                reject(new Error("Geolocation is not supported"));
              }
            });

          const location = await getLocation();
          latitude = location.latitude;
          longitude = location.longitude;
        } catch (error) {
          errorMessages.push(`Error fetching location details:${error}`);
        }

        setAnalyticsData({
          latitude,
          longitude,
          browserName,
          deviceName,
          network,
          isLoading: false,
          error: errorMessages.length > 0 ? errorMessages.join(", ") : null, 
        });
      } catch (error) {
        console.error("Error fetching analytics data:", error);
        setAnalyticsData((prevData) => ({
          ...prevData,
          isLoading: false,
          error: error.message || "Unknown error occured",
        }));
      }
    }, []);

  useEffect(() => {
    fetchAnalytics();
  }, [fetchAnalytics]);

  return { ...analyticsData, refetch: fetchAnalytics };
};

export default useDeviceAnalytics;
